import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { SmartLink } from "./SiteHeader";
import { REALTOR_LOGIN_HREF } from "./nav";
import { apiError, clientApi, useClientAuth } from "./portal/ClientAuth";

const FIELD = "cp-field";

function Field({ label, children }) {
  return <label className={FIELD}><span>{label}</span>{children}</label>;
}

// Only in-portal paths may be used as a post-login destination (e.g. ?next=/portal/application/ from the "Start my application" email CTA).
const safeNext = (v) => (v && /^\/portal\/[\w\-/?=&]*$/.test(v) ? v : null);

function SignIn({ onForgot, next }) {
  const { setClient } = useClientAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await clientApi.post("/auth/login", f); setClient(data); navigate(next || "/portal/"); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form className="cp-form" onSubmit={submit} data-testid="client-signin-form">
      <Field label="Email"><input type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="client-signin-email" /></Field>
      <Field label="Password"><input type="password" required autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="client-signin-password" /></Field>
      {err && <p className="cp-err" role="alert" data-testid="client-signin-error">{err}</p>}
      <button className="btn" type="submit" disabled={busy} data-testid="client-signin-submit">{busy ? "One sec…" : "Sign in →"}</button>
      <button type="button" className="cp-link" onClick={onForgot} data-testid="client-forgot-link">Forgot your password?</button>
    </form>
  );
}

function SignUp({ next }) {
  const { setClient } = useClientAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await clientApi.post("/auth/signup", f); setClient(data); navigate(next || "/portal/?welcome=1"); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <form className="cp-form" onSubmit={submit} data-testid="client-signup-form">
      <div className="cp-two">
        <Field label="First name"><input required autoComplete="given-name" value={f.first_name} onChange={set("first_name")} data-testid="client-signup-first" /></Field>
        <Field label="Last name"><input required autoComplete="family-name" value={f.last_name} onChange={set("last_name")} data-testid="client-signup-last" /></Field>
      </div>
      <Field label="Email"><input type="email" required autoComplete="email" value={f.email} onChange={set("email")} data-testid="client-signup-email" /></Field>
      <Field label="Phone"><input type="tel" required autoComplete="tel" placeholder="780-555-0123" value={f.phone} onChange={set("phone")} data-testid="client-signup-phone" /></Field>
      <Field label="Set a password"><input type="password" required minLength={10} autoComplete="new-password" placeholder="10+ characters, letters plus a number or symbol" value={f.password} onChange={set("password")} data-testid="client-signup-password" /></Field>
      {err && <p className="cp-err" role="alert" data-testid="client-signup-error">{err}</p>}
      <button className="btn" type="submit" disabled={busy} data-testid="client-signup-submit">{busy ? "Setting you up…" : "Create my account →"}</button>
    </form>
  );
}

function Forgot({ onBack }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await clientApi.post("/auth/forgot-password", { email }); setDone(data.message); }
    catch (ex) { setErr(apiError(ex)); }
  };
  return (
    <form className="cp-form" onSubmit={submit} data-testid="client-forgot-form">
      <p>Pop in your email and we'll send a one-hour link to pick a new password.</p>
      {done ? <p className="cp-ok" data-testid="client-forgot-done">{done}</p> : (
        <>
          <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="client-forgot-email" /></Field>
          {err && <p className="cp-err" role="alert">{err}</p>}
          <button className="btn" type="submit" data-testid="client-forgot-submit">Send the link →</button>
        </>
      )}
      <button type="button" className="cp-link" onClick={onBack}>← Back to sign in</button>
    </form>
  );
}

export default function ClientLoginPage() {
  const { client } = useClientAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const next = safeNext(params.get("next"));
  useEffect(() => { if (client) navigate(next || "/portal/", { replace: true }); }, [client, navigate, next]);

  return (
    <SiteLayout>
      <SeoHead title="Client Login | Hello Mortgage Team" description="Sign in to your Hello Mortgage account to pick up your application, see your options and find your documents." path="/client-login/" noindex ogImage="https://www.hellomortgage.ca/brand/client-login-hero.jpg" />
      <section className="calc-hero bb-hero" data-testid="client-login-page">
        <div className="wrap bb-hero-grid">
          {mode === "signup" ? (
            <div data-testid="client-signup-hero">
              <div className="eyebrow">Client Portal</div>
              <h1>Hello.<br />Let's get started.</h1>
            </div>
          ) : (
            <div data-testid="client-signin-hero">
              <div className="eyebrow">Client Login</div>
              <h1>Hello again.<br />Let's keep you moving.</h1>
              <p>Your options, your strategy, your documents to sign: one login, no digging through emails.</p>
              <p className="micro">Wipe your feet. Grab a coffee. We've got this.</p>
            </div>
          )}
          <img src={mode === "signup" ? "/brand/client-signup-hero.jpg" : "/brand/client-login-hero.jpg"} alt={mode === "signup" ? "Welcome — an open front door with a HELLO doormat and house keys." : "Welcome back — an open front door with a HELLO doormat and house keys."} className="bb-hero-art" data-testid="client-login-hero-art" />
        </div>
      </section>
      <section className="calc-body">
        <div className="wrap cp-grid">
          <div className="cp-card" data-testid="client-auth-card">
            {mode !== "forgot" && (
              <div className="cp-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={mode === "signin"} className={mode === "signin" ? "on" : ""} onClick={() => setMode("signin")} data-testid="client-tab-signin">I have an account</button>
                <button type="button" role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "on" : ""} onClick={() => setMode("signup")} data-testid="client-tab-signup">I'm new here</button>
              </div>
            )}
            {mode === "signin" && <SignIn onForgot={() => setMode("forgot")} next={next} />}
            {mode === "signup" && <SignUp next={next} />}
            {mode === "forgot" && <Forgot onBack={() => setMode("signin")} />}
          </div>
          <div className="cp-side">
            <div className="tool" data-testid="client-login-realtor">
              <span className="tag">Realtor?</span>
              <h3>The Realtor portal is over here.</h3>
              <SmartLink className="btn outline" href={REALTOR_LOGIN_HREF} data-testid="client-login-realtor-link">Realtor Login →</SmartLink>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
